<!-- HolidayDateInput.vue -->
<template>
  <li class="date-list-item">
    <div class="date-item">
      <n-date-picker
        ref="dateInput"
        v-model="dateValue"
        :min="new Date()"
        :max="new Date(3000, 11, 31)"
        @input="$v.date.$touch()"
        :error-message="getErrorMessage('Date')"
      />
      <div class="icon-container">
        <svg @click="deleteDate" class="trash-icon">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#trash" />
        </svg>
      </div>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import NDatePicker from '@/shared/designsystem/components/input/date-picker/date-picker.vue';
import { useVModel } from '@vueuse/core';

const props = defineProps<{
  modelValue: Date;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date): void;
  (e: 'delete'): void;
}>();

const dateValue = useVModel(props, 'modelValue', emit);
const dateInput = ref<InstanceType<typeof NDatePicker> | null>(null);

// Validation
const rules = {
  date: {
    required,
    todayOrFutureValid: VuelidateUtil.todayOrFuture,
  },
};

const $v = useVuelidate(rules, { date: dateValue });

function getErrorMessage(label: string): string | null {
  return VuelidateUtil.getErrorMessage($v, 'date', label, (failedRule) => {
    if (VuelidateUtil.isValidationInvalid($v, 'date', 'todayOrFutureValid'))
      return `${label} must be today or in the future`;
    return null;
  });
}

function deleteDate() {
  emit('delete');
}

// Expose for focusing
defineExpose({ focus: () => focusOnInput(dateInput.value) });

function focusOnInput(component?: InstanceType<typeof NDatePicker> | null) {
  if (component) {
    const inputElements = component.$el.getElementsByTagName('input');
    if (inputElements.length === 1) {
      inputElements[0].focus();
    }
  }
}
</script>

<style scoped lang="scss">
.date-list-item {
  padding: 0.25rem 0;
}

.date-item {
  display: flex;
}

.icon-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .trash-icon {
    width: 1.25em;
    height: 1.25em;
    fill: #c7cdd3;
    margin-left: 0.5em;
    cursor: pointer;

    &:hover {
      fill: #a4a6a9;
    }
  }
}
</style>
